import { Button } from "@dash/form";
import { Header2, Page } from "@dash/page";
import { QuerySkeleton } from "@dash/skeleton";
import { css, useTheme } from "@emotion/react";
import Decimal from "decimal.js";
import { MdAddBox } from "react-icons/md";

import { BarcodeScannerBox } from "./BarcodeScannerBox";
import { CancelShopifyOrderDialog } from "./packing-page/CancelShopifyOrderDialog";
import { packageWeightLimitKg } from "./packing-page/constants";
import { NoPackageDialog, OverweightDialog } from "./packing-page/OrderPackingDialogs";
import { OrderPackingHeaderBar } from "./packing-page/OrderPackingHeaderBar";
import { isOrderPackingLocked } from "./packing-page/orderPackingUtils";
import { PackageList } from "./packing-page/PackageList";
import { ScanDialog } from "./packing-page/ScanDialog";
import { ScheduleCourierDialog } from "./packing-page/ScheduleCourierDialog";
import { ToPackList } from "./packing-page/ToPackList";
import { useOrderPacking } from "./packing-page/useOrderPacking";

export const OrderPackingPage = () => {
  const theme = useTheme();
  const packing = useOrderPacking();
  const {
    orderQuery,
    order,
    orderId,
    packages,
    activeId,
    setActiveId,
    toPackItems,
    setScannedCode,
    setDialogOpen,
    showNoPackageDialog,
    setShowNoPackageDialog,
    showOverweightDialog,
    setShowOverweightDialog,
    showScheduleCourierDialog,
    setShowScheduleCourierDialog,
    showCancelShopifyDialog,
    setShowCancelShopifyDialog,
    queryClient,
    getTotalOrderQuantity,
    getDisplayName,
    getWeight,
    addItemToPackage,
    persistPackage,
    handleAddPackage,
    handleQuantityChange,
    closeScanDialog,
    handleConfirmScan,
    allPackagesHaveTracking,
    allItemsPacked,
    shouldShowCourierButton,
    isCourierAlreadyScheduled,
    isAddPackageDisabled,
    projectedWeightForScan,
    scheduleCourierMutation,
    addTrackingMutation,
    setPackages,
    scannedCode,
    isDialogOpen,
  } = packing;

  return (
    <>
      <QuerySkeleton query={orderQuery} height="58px" width="100%">
        {(orderData) => (
          <>
            <OrderPackingHeaderBar
              orderData={orderData}
              onOpenCancelShopify={() => setShowCancelShopifyDialog(true)}
              shouldShowCourierButton={shouldShowCourierButton}
              isCourierAlreadyScheduled={isCourierAlreadyScheduled}
              onScheduleCourier={() => setShowScheduleCourierDialog(true)}
            />

            <div
              css={css({
                marginTop: "58px",
                paddingTop: "1rem",
              })}
            >
              <Page
                css={css({
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                })}
              >
              {toPackItems.some((i) => i.quantity > 0) &&
              orderData.deliveryMethod !== "palletDelivery" &&
              !isOrderPackingLocked(orderData) ? (
                <BarcodeScannerBox
                  onScan={(code) => {
                    setScannedCode(code);
                    setDialogOpen(true);
                  }}
                />
              ) : null}

              {orderData.deliveryMethod === "palletDelivery" && <Header2>Zamowienie paletowe</Header2>}

              <ScanDialog
                scannedCode={scannedCode}
                isOpen={isDialogOpen}
                isPackageSelected={activeId !== null}
                toPackItems={toPackItems}
                projectedWeight={projectedWeightForScan}
                onClose={closeScanDialog}
                onConfirm={handleConfirmScan}
                getDisplayName={getDisplayName}
              />

              {allPackagesHaveTracking &&
                allItemsPacked &&
                !isAddPackageDisabled(orderData) && (
                  <div css={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      onClick={() =>
                        addTrackingMutation.mutate(
                          { orderId: orderData.id },
                          { onSuccess: () => queryClient.invalidateQueries(orderQuery.key) },
                        )
                      }
                      isLoading={addTrackingMutation.isLoading}
                    >
                      Potwierdź zamówienie
                    </Button>
                  </div>
                )}

              <PackageList
                queryKey={orderQuery.key}
                order={orderData}
                packages={packages}
                activeId={activeId}
                orderItems={orderData.items}
                getTotalOrderQuantity={getTotalOrderQuantity}
                onSetActiveId={setActiveId}
                onQuantityChange={handleQuantityChange}
                getDisplayName={getDisplayName}
              />

              <ToPackList
                order={orderData}
                items={toPackItems}
                isPackingLocked={isOrderPackingLocked(orderData)}
                getTotalOrderQuantity={getTotalOrderQuantity}
                activeId={activeId}
                onAddItem={(item) => {
                  if (activeId === null) return;
                  const currentPackage = packages[activeId - 1];
                  const currentWeight = getWeight(currentPackage);
                  const projectedWeight = new Decimal(currentWeight).plus(item.weight ?? 0).toNumber();
                  if (projectedWeight > packageWeightLimitKg) {
                    setShowOverweightDialog(true);
                    return;
                  }
                  const updated = packages.map((pkg, i) =>
                    i === activeId - 1 ? addItemToPackage(pkg, item) : pkg,
                  );
                  setPackages(updated);
                  if (orderId) persistPackage(updated[activeId - 1]);
                }}
                getDisplayName={getDisplayName}
              />

              <Button
                icon={<MdAddBox />}
                onClick={handleAddPackage}
                variant="outline"
                size="large"
                isDisabled={isAddPackageDisabled(orderData)}
                css={{
                  border: `1px dashed ${theme.colors.primary}`,
                  marginBottom: "18px",
                }}
              >
                Nowa paczka
              </Button>
              </Page>
            </div>
          </>
        )}
      </QuerySkeleton>

      {showNoPackageDialog && (
        <NoPackageDialog onClose={() => setShowNoPackageDialog(false)} />
      )}
      {showOverweightDialog && (
        <OverweightDialog onClose={() => setShowOverweightDialog(false)} />
      )}

      {order && (
        <ScheduleCourierDialog
          isOpen={showScheduleCourierDialog}
          onClose={() => setShowScheduleCourierDialog(false)}
          onConfirm={(data) => {
            scheduleCourierMutation.mutate(
              { orderId: order.id, ...data },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries(orderQuery.key);
                  setShowScheduleCourierDialog(false);
                },
              },
            );
          }}
        />
      )}

      {order && (
        <CancelShopifyOrderDialog
          isOpen={showCancelShopifyDialog}
          onClose={() => setShowCancelShopifyDialog(false)}
          order={order}
          queryKey={orderQuery.key}
        />
      )}
    </>
  );
};
